import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";

/* Valtech Custom Code */
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { dispatchProductSearchedClickEvent } from "@store/Services/AnalyticsService";
import { PRODUCT, TYPOGRAPHY } from "@store/theme/design";
import { imageStyles } from "@store/theme/mixins";
import searchResultProps, { searchResultGetGridLinkProps } from "@store/theme/props/searchResult";

const StyledSpan = getStyledWrapper("span");
/* Valtech Custom Code */

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // Valtech Custom Code
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    imageWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: searchResultProps.title,
    link: searchResultGetGridLinkProps(searchResultProps.link),
    focusedLink: searchResultGetGridLinkProps(searchResultProps.linkFocus),
    imageWrapper: {
        css: css`
            flex: 0 0 auto;
            width: 50px;
            ${imageStyles()}
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1 0 auto;
            width: 0%;
            margin-left: 10px;
        `,
    },
    title: {
        ellipsis: true,
        weight: TYPOGRAPHY.weight.bold,
    },
    erpNumberText: {
        ellipsis: true,
        size: TYPOGRAPHY.size.smallest,
        css: css`
            margin-top: 3px;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;

        /* Valtech Custom Code */
        const handleAnalyticsClick = (product: ProductAutocompleteItemModel) => {
            if (product) {
                dispatchProductSearchedClickEvent(product);
            }
        };
        const onClick = (
            event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
            product: ProductAutocompleteItemModel,
        ) => {
            event.preventDefault();
            const productDetailPath = product.url;
            handleAnalyticsClick(product);

            if (!productDetailPath) {
                return;
            }
            this.props.goToUrl(productDetailPath);
        };
        /* Valtech Custom Code */

        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography>
                {products.map(product => (
                    /* Valtech Custom Code */
                    <Link
                        {...(this.props.focusedItem === product ? styles.focusedLink : styles.link)}
                        data-test-selector={`productId_${product.id}`}
                        className="autocomplete-products-link"
                        key={product.id!}
                        // Valtech Custom Code
                        onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                            onClick(event, product);
                        }}
                    >
                        <StyledSpan {...styles.imageWrapper}>
                            <LazyImage
                                {...styles.image}
                                src={product.image || PRODUCT.image.small.defaultPath}
                                className="autocomplete-products-image"
                            />
                        </StyledSpan>
                        <StyledSpan {...styles.infoWrapper}>
                            <Typography {...styles.title}>{product.displayTitle}</Typography>
                            <Typography
                                {...styles.erpNumberText}
                                data-test-selector="autocompleteProducts_PartNumber"
                                className="autocomplete-products-erp-number"
                            >
                                {translate("Part #")} - {product.displayErpNumber}
                            </Typography>
                        </StyledSpan>
                    </Link>
                    /* Valtech Custom Code */
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
